import { createContext, useContext, useReducer } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { checkMobile } from '../utils'
import { login } from './login'

const initialState = {
  isMobile: checkMobile(),
  canvasCrossOrigin: !['kow.onemt.com', 'h5action-test.onemt.co'].includes(
    window.location.host
  ),
  toastData: {
    open: false,
    dialog: false,
    content: '',
    timeout: 1000,
  },
  pendingData: {
    open: false,
  },
  modalData: {
    ruleOpen: false,
    recallOpen: window.queryObj.modal === 'recall' ? true : false,
    recordOpen: window.queryObj.modal === 'record' ? true : false,
    shareOpen: window.queryObj.modal === 'share' ? true : false,
    selectRoleOpen: false,
    rewardOpen: window.queryObj.modal === 'reward' ? true : false,
  },
  alertData: {
    open: false,
    freeze: false,
    btnColor: '' as 'blue',
    content: '',
    hint: '',
    showLogoutBtn: false,
    resolveText: '',
    rejectText: '',
    resolveClose: false,
    rejectClose: false,
    onResolve: null as () => void | null,
    onReject: null as () => void | null,
  } as AlertEvent,
  openWebLogin: false,
  code: getCode(),
  login,
  serverTime: 0,
  startTime: 0,
  endTime: 0,
  coupon: {
    level: 1 as CouponLevel,
    count: 0,
  },
  bindRewards: [] as RewardList,
  bindRewardViews: [] as RewardViewList,
  tasks: [] as TaskList,
  friends: [] as Friends,
}

type AppState = typeof initialState

function getCode() {
  return window.queryObj.invitationCode || ''
}

function stateReducer(state: AppState, action: Parameters<StoreDispatch>[0]) {
  const newState = cloneDeep(state)
  switch (action.type) {
    case 'openWebLogin':
      return Object.assign(newState, cloneDeep(action.data))
    case 'setPendingData':
      newState.pendingData = Object.assign(newState.pendingData, action.data)
      return newState
    case 'setToastData':
      newState.toastData = Object.assign(
        newState.toastData,
        cloneDeep(action.data)
      )
      return newState
    case 'setYoutubePlayerData':
      return Object.assign(newState, cloneDeep(action.data))
    case 'setAlertData':
      newState.alertData = cloneDeep(action.data)
      return newState
    case 'updateModal':
      newState.modalData = Object.assign(
        newState.modalData,
        cloneDeep(action.data)
      )
      return newState
    case 'closeModal':
      Object.keys(newState.modalData).forEach(
        (key: keyof typeof newState.modalData) =>
          (newState.modalData[key] = false)
      )
      return newState
    case 'updateTime':
      return Object.assign(newState, cloneDeep(action.data))
    case 'setLoginData':
      newState.login = Object.assign(newState.login, cloneDeep(action.data))
      return newState
    case 'login':
      newState.login.logined = action.data.logined as boolean
      newState.login.userInfo.sessionId = action.data.token as string
      newState.login.userInfo.checked = 0
      return newState
    case 'logout':
      newState.login.logined = window.inSDK
      newState.login.userInfo.sessionId = ''
      newState.login.userInfo.playerId = ''
      newState.login.userInfo.serverId = ''
      newState.login.userInfo.playerName = ''
      newState.login.userInfo.isBinded = false
      newState.login.userInfo.isCodeBinded = false
      newState.code = getCode()
      return newState
    case 'bindPlayer':
      newState.login.userInfo = Object.assign(
        newState.login.userInfo,
        cloneDeep(action.data)
      )
      return newState
    case 'setZoneId':
      newState.login.userInfo.zoneId = action.data.zoneId as Zone['zoneId']
      return newState
    case 'setPlayer':
      newState.login.userInfo.playerType = action.data.playerType as PlayerType
      newState.login.userInfo.playerId = action.data.playerId as string
      newState.login.userInfo.playerName = action.data.playerName as string
      newState.login.userInfo.serverId = action.data.serverId as string
      newState.login.userInfo.serverName = action.data.serverName as string
      newState.login.userInfo.isBinded = action.data.isBinded as boolean
      newState.login.userInfo.justBinded = true
      return newState
    case 'bindCode':
      newState.login.userInfo.isCodeBinded = action.data.isCodeBinded as boolean
      return newState
    case 'setCode':
      newState.code = action.data.code as string
      return newState
    case 'setBaseData':
      return Object.assign(newState, action.data)
    case 'syncData':
      newState.friends = action.data.friends as Friends
      ;(action.data.tasks as TaskList).forEach((obj) => {
        const target = newState.tasks.find((item) => item.id === obj.id)
        target.current = obj.current
        target.total = obj.total
        target.status = obj.status
      })
      return newState
    case 'play':
      newState.coupon = action.data as typeof newState.coupon
      return newState
    default: {
      throw Error(`Unknown state action: ${action.type}`)
    }
  }
}

export function useDispatch() {
  const [state, dispatch] = useReducer(stateReducer, initialState)

  return { state, dispatch }
}

export const StateContext = createContext<AppState>(initialState)
export const DispatchContext = createContext<StoreDispatch>(null)

export function useStore() {
  const state = useContext(StateContext)
  const dispatch = useContext(DispatchContext)

  return { state, dispatch }
}
